import React, { ReactNode, useEffect, useState } from "react";
import { UserDetails, UserRole } from "../types/authentication.ts";
import { useOktaAuth } from "@okta/okta-react";
import { getUserInfo } from "../api/AuthenticatedClient.ts";

type UserContextType = {
  userDetails?: UserDetails;
  roles?: UserRole[];
  logout: () => void;
};

const UserContext = React.createContext<UserContextType | undefined>(undefined);

type UserProviderProps = {
  children?: ReactNode;
};

const UserProvider: React.FC<UserProviderProps> = ({ ...props }) => {
  const [userDetails, setUserDetails] = useState<UserDetails>();
  const [roles, setRoles] = useState<UserRole[]>();
  const { authState } = useOktaAuth();

  const logout = () => {
    localStorage.clear();
    setUserDetails(undefined);
    setRoles(undefined);
    window.location.reload();
  };

  useEffect(() => {
    // if user is authenticated, fetch user details
    if (authState?.isAuthenticated === true) {
      getUserInfo().then(setUserDetails).catch(console.error);
    }

    // if user is not authenticated, clear user details and roles
    if (authState?.isAuthenticated === false) {
      setUserDetails(undefined);
      setRoles(undefined);
    }
  }, [authState?.isAuthenticated]);

  useEffect(() => {
    if (userDetails?.authorities) {
      setRoles(userDetails.authorities.map(({ authority }) => authority) as UserRole[]);
    }
  }, [userDetails?.authorities]);

  return <UserContext.Provider value={{ userDetails, roles, logout }} {...props} />;
};

const useUser = (): UserContextType => {
  const context: UserContextType | undefined = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error(`useUser must be used within a UserProvider`);
  }
  return context;
};

export { UserProvider, useUser };
